import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../AuthContext";
import { LOCALES } from "../i18n";
import api, { errMsg } from "../api";
import DropZone from "../components/DropZone";
import {
    Folder, FolderPlus, FileText, Upload, Download, Trash2, Lock, Users as UsersIcon,
    ChevronRight, Home, Loader2, MoreVertical, FileImage, FileVideo, File as FileIcon
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const fileIcon = (mime) => {
    if (!mime) return FileIcon;
    if (mime.startsWith("image/")) return FileImage;
    if (mime.startsWith("video/")) return FileVideo;
    return FileText;
};

const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const u = ["B", "KB", "MB", "GB"];
    let i = 0; let n = bytes;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

export default function DrivePage() {
    const { user, lang } = useAuth();
    const [folders, setFolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [newFolderOpen, setNewFolderOpen] = useState(false);
    const [permissionsFor, setPermissionsFor] = useState(null);
    const fileInputRef = useRef(null);

    const load = useCallback(async (folderId) => {
        setLoading(true);
        try {
            const [fol, fil] = await Promise.all([
                api.get(`/drive/folders${folderId ? `?parent_id=${folderId}` : ""}`),
                api.get(`/drive/files${folderId ? `?folder_id=${folderId}` : ""}`),
            ]);
            setFolders(fol.data); setFiles(fil.data);
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(currentFolder?.id || null); }, [currentFolder, load]);

    const navigate = (folder) => {
        if (!folder) {
            setCurrentFolder(null); setBreadcrumbs([]);
        } else {
            setCurrentFolder(folder);
            setBreadcrumbs((bc) => {
                const idx = bc.findIndex((b) => b.id === folder.id);
                if (idx >= 0) return bc.slice(0, idx + 1);
                return [...bc, folder];
            });
        }
    };

    const uploadFileObject = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        if (currentFolder) fd.append("folder_id", currentFolder.id);
        try {
            await api.post("/drive/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(lang === "de" ? "Datei hochgeladen" : "Uploaded");
            load(currentFolder?.id || null);
        } catch (err) { toast.error(errMsg(err, "Upload failed")); }
    };
    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await uploadFileObject(file);
        e.target.value = "";
    };

    const downloadFile = async (f) => {
        try {
            const token = localStorage.getItem("taskflow_token");
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drive/files/${f.id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = f.name;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (err) { toast.error("Download failed"); }
    };

    const delFile = async (f) => {
        if (!window.confirm(lang === "de" ? `"${f.name}" löschen?` : `Delete "${f.name}"?`)) return;
        try { await api.delete(`/drive/files/${f.id}`); toast.success("OK"); load(currentFolder?.id || null); }
        catch (err) { toast.error(errMsg(err, "Error")); }
    };

    const delFolder = async (f) => {
        if (!window.confirm(lang === "de" ? `Ordner "${f.name}" und alle Inhalte löschen?` : `Delete folder?`)) return;
        try { await api.delete(`/drive/folders/${f.id}`); toast.success("OK"); load(currentFolder?.id || null); }
        catch (err) { toast.error(errMsg(err, "Error")); }
    };

    return (
        <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="drive-page">
            <header className="mb-4 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-display font-bold flex items-center gap-2"><Folder className="w-7 h-7" /> Drive</h1>
                    <p className="text-xs text-neutral-500 mt-0.5">{lang === "de" ? "Ordner, Dateien & Berechtigungen" : "Folders, files & permissions"}</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => setNewFolderOpen(true)} variant="outline" size="sm" className="h-9" data-testid="drive-new-folder-btn"><FolderPlus className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Neuer Ordner" : "New folder"}</Button>
                    <Button onClick={() => fileInputRef.current?.click()} size="sm" className="h-9 bg-blue-600 hover:bg-blue-700" data-testid="drive-upload-btn"><Upload className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Datei hochladen" : "Upload"}</Button>
                    <input ref={fileInputRef} type="file" hidden onChange={upload} data-testid="drive-file-input" />
                </div>
            </header>

            {/* Breadcrumbs */}
            <div className="mb-3 flex items-center gap-1 text-sm flex-wrap" data-testid="drive-breadcrumbs">
                <button onClick={() => navigate(null)} className="flex items-center gap-1 hover:bg-neutral-100 px-2 py-1 rounded">
                    <Home className="w-3.5 h-3.5" /> {lang === "de" ? "Drive" : "Drive"}
                </button>
                {breadcrumbs.map((b) => (
                    <React.Fragment key={b.id}>
                        <ChevronRight className="w-3.5 h-3.5 text-neutral-300" />
                        <button onClick={() => navigate(b)} className="hover:bg-neutral-100 px-2 py-1 rounded font-medium">{b.name}</button>
                    </React.Fragment>
                ))}
            </div>

            {/* Drop zone */}
            <DropZone
                accept="*/*"
                onFile={uploadFileObject}
                compact
                label={lang === "de" ? "Datei hierher ziehen, Str+V einfügen oder klicken" : "Drop file here, paste with Ctrl+V, or click"}
                testId="drive-dropzone"
                className="mb-3"
            />

            {/* Content */}
            <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
                {loading ? <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
                : (folders.length === 0 && files.length === 0) ? <div className="p-12 text-center text-sm text-neutral-500">{lang === "de" ? "Leer — neuen Ordner anlegen oder Datei hochladen" : "Empty"}</div>
                : (
                    <div className="divide-y divide-neutral-100">
                        {folders.map((f) => (
                            <div key={f.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 group" data-testid={`drive-folder-${f.id}`}>
                                <button onClick={() => navigate(f)} className="flex items-center gap-3 flex-1 text-left">
                                    <Folder className="w-5 h-5 text-blue-600" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm">{f.name}</p>
                                        <p className="text-[10px] text-neutral-500">{lang === "de" ? "Ordner" : "Folder"} · {f.created_by_name} · {(f.permissions || []).length} {lang === "de" ? "Freigaben" : "permissions"}</p>
                                    </div>
                                </button>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button onClick={() => setPermissionsFor(f)} variant="ghost" size="sm" className="h-7 px-2" data-testid={`drive-perm-${f.id}`} title={lang === "de" ? "Berechtigungen" : "Permissions"}><Lock className="w-3.5 h-3.5" /></Button>
                                    <Button onClick={() => delFolder(f)} variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                            </div>
                        ))}
                        {files.map((f) => {
                            const Icon = fileIcon(f.mime_type);
                            return (
                                <div key={f.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 group" data-testid={`drive-file-${f.id}`}>
                                    <Icon className="w-5 h-5 text-neutral-500" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm">{f.name}</p>
                                        <p className="text-[10px] text-neutral-500">{formatSize(f.size)} · {f.uploaded_by_name} · {new Date(f.uploaded_at).toLocaleDateString(LOCALES[lang])}</p>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button onClick={() => downloadFile(f)} variant="ghost" size="sm" className="h-7 px-2"><Download className="w-3.5 h-3.5" /></Button>
                                        <Button onClick={() => delFile(f)} variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <NewFolderDialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} parentId={currentFolder?.id} onCreated={() => { setNewFolderOpen(false); load(currentFolder?.id || null); }} lang={lang} />
            <PermissionsDialog folder={permissionsFor} onClose={() => setPermissionsFor(null)} onSaved={() => { setPermissionsFor(null); load(currentFolder?.id || null); }} lang={lang} />
        </div>
    );
}

function NewFolderDialog({ open, onClose, parentId, onCreated, lang }) {
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setName(""); }, [open]);
    const create = async () => {
        if (!name.trim()) return;
        setBusy(true);
        try {
            await api.post("/drive/folders", { name, parent_id: parentId || null });
            toast.success("OK");
            onCreated();
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm" data-testid="drive-new-folder-dialog">
                <DialogHeader><DialogTitle className="font-display">{lang === "de" ? "Neuer Ordner" : "New folder"}</DialogTitle><DialogDescription className="text-xs">{lang === "de" ? "Berechtigungen kannst du anschließend setzen." : "Permissions can be set after."}</DialogDescription></DialogHeader>
                <div><Label className="text-xs">{lang === "de" ? "Name" : "Name"}</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" autoFocus data-testid="drive-folder-name" /></div>
                <DialogFooter><Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button><Button onClick={create} disabled={busy || !name.trim()} className="bg-neutral-900 hover:bg-neutral-800" data-testid="drive-folder-create">{busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}{lang === "de" ? "Erstellen" : "Create"}</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PermissionsDialog({ folder, onClose, onSaved, lang }) {
    const [users, setUsers] = useState([]);
    const [perms, setPerms] = useState([]);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!folder) return;
        Promise.all([
            api.get("/users").then((r) => r.data).catch(() => []),
            api.get(`/drive/folders/${folder.id}/permissions`).then((r) => r.data.permissions || []).catch(() => []),
        ]).then(([u, p]) => { setUsers(u); setPerms(p); });
    }, [folder]);

    const setAccess = (uid, access) => {
        setPerms((prev) => {
            const out = prev.filter((p) => p.user_id !== uid);
            if (access) out.push({ user_id: uid, access });
            return out;
        });
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/drive/folders/${folder.id}`, { permissions: perms });
            toast.success("OK");
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setBusy(false); }
    };

    if (!folder) return null;
    return (
        <Dialog open={!!folder} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="drive-perm-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><Lock className="w-4 h-4" /> {lang === "de" ? "Berechtigungen" : "Permissions"}</DialogTitle>
                    <DialogDescription className="text-xs"><strong>{folder.name}</strong> — {lang === "de" ? "Admin/Master sieht immer alles. Eigentümer hat Vollzugriff." : "Admin/Master see everything. Owner has full access."}</DialogDescription>
                </DialogHeader>
                <div className="space-y-1.5">
                    {users.map((u) => {
                        const cur = perms.find((p) => p.user_id === u.id)?.access || "";
                        return (
                            <div key={u.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-neutral-50" data-testid={`drive-perm-row-${u.id}`}>
                                <div className="flex-1">
                                    <p className="text-sm font-medium">{u.name}</p>
                                    <p className="text-[10px] text-neutral-500 uppercase">{u.role}</p>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => setAccess(u.id, "")} className={`px-2 py-1 rounded text-xs ${!cur ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600"}`}>{lang === "de" ? "Kein" : "None"}</button>
                                    <button onClick={() => setAccess(u.id, "read")} className={`px-2 py-1 rounded text-xs ${cur === "read" ? "bg-blue-600 text-white" : "bg-neutral-100 text-neutral-600"}`} data-testid={`drive-perm-${u.id}-read`}>{lang === "de" ? "Lesen" : "Read"}</button>
                                    <button onClick={() => setAccess(u.id, "write")} className={`px-2 py-1 rounded text-xs ${cur === "write" ? "bg-emerald-600 text-white" : "bg-neutral-100 text-neutral-600"}`} data-testid={`drive-perm-${u.id}-write`}>{lang === "de" ? "Schreiben" : "Write"}</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <DialogFooter><Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button><Button onClick={save} disabled={busy} className="bg-neutral-900 hover:bg-neutral-800" data-testid="drive-perm-save">{busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}{lang === "de" ? "Speichern" : "Save"}</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
